import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	Grid,
} from "@mui/material";
import { useSelector } from "react-redux";

import DropdownController from "../../components/inputs/DropdownController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { maxLengthMessage } from "../../utils/functions";

const restockSchema = yup.object().shape({
	quantity: yup
		.number()
		.required("Quantity is required")
		.min(1, "Quantity must be at least 1")
		.typeError("Quantity must be a number"),
	vendor: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
	unitCost: yup
		.number()
		.nullable()
		.min(0, "Unit cost cannot be negative")
		.transform((v, o) => (o === "" ? null : v)),
	fundingSourceId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	purchaseCost: yup
		.number()
		.nullable()
		.min(0, "Purchase cost cannot be negative")
		.transform((v, o) => (o === "" ? null : v)),
	notes: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
});

const buildDefaults = (item) => {
	const suggestedQty =
		item?.maxStock != null
			? Math.max(0, item.maxStock - (item.unitsInStock || 0))
			: "";

	return {
		quantity: suggestedQty,
		vendor: item?.vendor || "",
		unitCost: item?.cost != null ? item.cost : "",
		fundingSourceId: item?.fundingSourceId || "",
		purchaseCost: "",
		notes: "",
	};
};

const RestockItemDialog = ({
	open,
	onClose,
	onSave,
	item,
	fundingSourceOptions = [],
}) => {
	const userAgency = useSelector((state) => state.user.userAgency);

	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(restockSchema),
		defaultValues: buildDefaults(item),
	});

	// Watch values for calculations and update indicators
	const quantity = watch("quantity");
	const unitCost = watch("unitCost");
	const purchaseCost = watch("purchaseCost");
	const vendor = watch("vendor");
	const fundingSourceId = watch("fundingSourceId");

	// Reset form when dialog opens with new data
	useEffect(() => {
		if (open && item) {
			reset(buildDefaults(item));
		}
	}, [item, open, reset]);

	// Clear form on close
	useEffect(() => {
		if (!open) reset();
	}, [open, reset]);

	// Calculate auto purchase cost
	const autoPurchaseCost = useMemo(() => {
		const qty = Number(quantity) || 0;
		const cost = Number(unitCost) || 0;
		if (qty > 0 && cost > 0) {
			return qty * cost;
		}
		return null;
	}, [quantity, unitCost]);

	// New stock level calculation
	const newStockLevel = useMemo(() => {
		const currentStock = item?.unitsInStock || 0;
		const qty = Number(quantity) || 0;
		return currentStock + qty;
	}, [item?.unitsInStock, quantity]);

	// Check if values differ from original item
	const vendorChanged = vendor && vendor !== (item?.vendor || "");
	const unitCostChanged =
		unitCost !== "" && Number(unitCost) !== (item?.cost ?? null);
	const fundingChanged =
		fundingSourceId &&
		Number(fundingSourceId) !== (item?.fundingSourceId ?? null);

	// Format currency for display
	const fmtCurrency = (n) =>
		n != null && !isNaN(n)
			? `$${Number(n).toLocaleString(undefined, {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})}`
			: "—";

	const onSubmit = (data) => {
		const qty = Number(data.quantity) || 0;
		const newUnitCost = data.unitCost !== "" ? Number(data.unitCost) : null;
		const finalPurchaseCost =
			data.purchaseCost !== ""
				? Number(data.purchaseCost)
				: newUnitCost != null && qty > 0
					? qty * newUnitCost
					: null;

		const payload = {
			// Issuance data
			quantity: qty,
			purchaseCost: finalPurchaseCost,
			vendor: data.vendor?.trim() || null,
			fundingSourceId: data.fundingSourceId
				? Number(data.fundingSourceId)
				: null,
			notes: data.notes?.trim() || null,

			// Item updates (if changed)
			updateItem: {
				vendor: vendorChanged ? data.vendor?.trim() : undefined,
				cost: unitCostChanged ? newUnitCost : undefined,
				fundingSourceId: fundingChanged
					? Number(data.fundingSourceId)
					: undefined,
			},

			agencyId: userAgency,
		};

		onSave(payload);
		onClose();
	};

	if (!item) return null;

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>
				Restock — {item.trackingNumber} · {item.description}
			</DialogTitle>

			<DialogContent>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					sx={{ mt: 1 }}
				>
					{/* ── Item Summary ── */}
					<Box
						sx={{
							display: "flex",
							gap: 2,
							p: 2,
							bgcolor: "background.paper",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							alignItems: "center",
							mb: 2,
						}}
					>
						<Typography sx={{ fontSize: 24 }}>📦</Typography>
						<Box sx={{ flex: 1 }}>
							<Typography fontWeight={700} fontSize={14}>
								{item.description}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 0.5, display: "block" }}
							>
								Current Stock:{" "}
								<Box
									component="span"
									sx={{
										fontWeight: 700,
										color:
											(item.unitsInStock || 0) <=
											(item.minBeforeRestock || 0)
												? "error.main"
												: "success.main",
									}}
								>
									{item.unitsInStock ?? 0}
								</Box>
								{item.maxStock != null && (
									<>
										<Box component="span" sx={{ ml: 1.5 }}>
											Max:{" "}
											<strong>{item.maxStock}</strong>
										</Box>
										<Box
											component="span"
											sx={{
												ml: 1.5,
												color: "primary.main",
											}}
										>
											Suggested:{" "}
											<strong>
												+
												{Math.max(
													0,
													item.maxStock -
														(item.unitsInStock ||
															0),
												)}
											</strong>
										</Box>
									</>
								)}
							</Typography>
						</Box>
					</Box>

					{/* ── Form Fields ── */}
					<Grid container spacing={2}>
						{/* Quantity */}
						<Grid item xs={12}>
							<TextFieldController
								name="quantity"
								label="Quantity to Add"
								control={control}
								errors={errors}
								type="number"
								placeholder="0"
								required
							/>
							{Number(quantity) > 0 && (
								<Typography
									variant="caption"
									sx={{
										mt: 0.5,
										display: "block",
										color: "success.main",
									}}
								>
									New stock level: {newStockLevel}
									{item.maxStock != null
										? ` / ${item.maxStock}`
										: ""}
								</Typography>
							)}
						</Grid>

						{/* Vendor */}
						<Grid item xs={12} sm={6}>
							<TextFieldController
								name="vendor"
								label={
									<>
										Vendor
										{vendorChanged && (
											<Box
												component="span"
												sx={{
													fontSize: 10,
													color: "primary.main",
													ml: 0.5,
												}}
											>
												← updates item
											</Box>
										)}
									</>
								}
								control={control}
								errors={errors}
								placeholder={item.vendor || "Vendor name..."}
							/>
						</Grid>

						{/* Unit Cost */}
						<Grid item xs={12} sm={6}>
							<TextFieldController
								name="unitCost"
								label={
									<>
										Unit Cost ($)
										{unitCostChanged && (
											<Box
												component="span"
												sx={{
													fontSize: 10,
													color: "primary.main",
													ml: 0.5,
												}}
											>
												← updates item
											</Box>
										)}
									</>
								}
								control={control}
								errors={errors}
								type="number"
								placeholder={
									item.cost != null
										? String(item.cost)
										: "0.00"
								}
							/>
						</Grid>

						{/* Funding Source */}
						<Grid item xs={12} sm={6}>
							<DropdownController
								name="fundingSourceId"
								label={
									<>
										Funding Source
										{fundingChanged && (
											<Box
												component="span"
												sx={{
													fontSize: 10,
													color: "primary.main",
													ml: 0.5,
												}}
											>
												← updates item
											</Box>
										)}
									</>
								}
								options={fundingSourceOptions}
								control={control}
								errors={errors}
							/>
						</Grid>

						{/* Total Purchase Cost */}
						<Grid item xs={12} sm={6}>
							<TextFieldController
								name="purchaseCost"
								label="Total Purchase Cost ($)"
								control={control}
								errors={errors}
								type="number"
								placeholder={
									autoPurchaseCost != null
										? `Auto: ${fmtCurrency(autoPurchaseCost)}`
										: "Enter or auto-calculated"
								}
							/>
							{Number(quantity) > 0 &&
								unitCost !== "" &&
								!purchaseCost && (
									<Typography
										variant="caption"
										sx={{
											mt: 0.5,
											display: "block",
											color: "success.main",
										}}
									>
										Calculated: {quantity} ×{" "}
										{fmtCurrency(unitCost)} ={" "}
										<strong>
											{fmtCurrency(autoPurchaseCost)}
										</strong>
									</Typography>
								)}
						</Grid>

						{/* Notes */}
						<Grid item xs={12}>
							<TextFieldController
								name="notes"
								label="Notes"
								control={control}
								errors={errors}
								placeholder="PO number, delivery info..."
								multiline
								rows={2}
							/>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATSaveButton
					onClick={handleSubmit(onSubmit)}
					tooltipText="Confirm Restock"
				/>
				<PATCloseButton onClick={onClose} tooltipText="Cancel" />
			</DialogActions>
		</Dialog>
	);
};

export default RestockItemDialog;
